import * as React from 'react';

const { useState, useEffect } = React;

export interface AnchorState {
  /** null once we've given up waiting, undefined while still looking. */
  rect: DOMRect | null | undefined;
  element: HTMLElement | null;
}

/**
 * Resolves a step's anchor to a live viewport rect.
 *
 * Two things make this more than a querySelector:
 *  - The anchor may not exist yet. Steps open panels, and the element they
 *    point at mounts a beat later, so we watch the DOM until it appears or the
 *    timeout expires.
 *  - The same selector can match more than once. The nav Chat link is mounted
 *    twice (a mobile copy and a desktop copy) and only one is displayed, so we
 *    take the first element that is actually rendered.
 */
export function useAnchorRect(
  selector: string | null,
  timeoutMs = 2000,
  /** Bump to force a re-resolve (e.g. on step change). */
  nonce = 0,
): AnchorState {
  const [state, setState] = useState<AnchorState>({ rect: undefined, element: null });

  useEffect(() => {
    if (!selector) {
      setState({ rect: undefined, element: null });
      return undefined;
    }

    let cancelled = false;
    let observer: MutationObserver | null = null;
    let timer: number | undefined;

    const visibleMatch = (): HTMLElement | null => {
      const matches = Array.from(document.querySelectorAll<HTMLElement>(selector));
      // offsetParent is null for display:none and for anything inside it, which
      // is exactly how the hidden nav copy presents.
      return matches.find((el) => el.offsetParent !== null || el.getClientRects().length > 0) || null;
    };

    const settle = (el: HTMLElement) => {
      if (cancelled) return;
      observer?.disconnect();
      window.clearTimeout(timer);
      el.scrollIntoView({ block: 'center', inline: 'nearest' });
      // Read the rect after the scroll has been applied.
      window.requestAnimationFrame(() => {
        if (cancelled) return;
        setState({ rect: el.getBoundingClientRect(), element: el });
      });
    };

    const found = visibleMatch();
    if (found) {
      settle(found);
    } else {
      observer = new MutationObserver(() => {
        const el = visibleMatch();
        if (el) settle(el);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      timer = window.setTimeout(() => {
        if (cancelled) return;
        observer?.disconnect();
        setState({ rect: null, element: null });
      }, timeoutMs);
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      window.clearTimeout(timer);
    };
  }, [selector, timeoutMs, nonce]);

  // Keep the cutout glued to the anchor while the page moves under it.
  useEffect(() => {
    const el = state.element;
    if (!el) return undefined;

    const sync = () => setState((prev) => (prev.element ? { ...prev, rect: el.getBoundingClientRect() } : prev));
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [state.element]);

  return state;
}
